import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { MissionProvider, FeedbackPage } from '@pilot-hub/ui'
import { LoginPagePilot } from './pages/LoginPage'
import { FlightCapturePage } from './pages/FlightCapturePage'
import { FlightLogPage } from './pages/FlightLogPage'
import { TelemetryPage } from './pages/TelemetryPage'
import { TacticalMapPage } from './pages/TacticalMapPage'
import { CoPilotTacticalMapPage } from './pages/CoPilotTacticalMapPage'
import { CoPilotCockpitPage } from './pages/CoPilotCockpitPage'
import { CoPilotMissionsplanerPage } from './pages/CoPilotMissionsplanerPage'
import { CoPilotChecklistPage } from './pages/CoPilotChecklistPage'
import { AirspaceObservationPage } from './pages/AirspaceObservationPage'
import { MissionDetailPage } from './pages/MissionDetailPage'
import { SettingsPage } from './pages/SettingsPage'
import { useAuth } from '@pilot-hub/auth'
import { Layout } from './components/Layout'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  if (user) {
    // Default route based on pilotMode (stored in localStorage)
    const pilotMode = (localStorage.getItem('pilot-hub-pilot-mode') as 'PILOT' | 'CO_PILOT') || 'PILOT'
    const defaultPath = pilotMode === 'PILOT' ? '/flugbuch' : '/flug-erfassen'
    return <Navigate to={defaultPath} replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  const router = useNavigate()
  // Expose router for LoginPage redirect without full reload
  ;(window as any).__PILOT_HUB_ROUTER__ = { navigate: router }
  return (
    <MissionProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPagePilot />
            </PublicRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/flug-erfassen" replace />} />
          <Route path="flug-erfassen" element={<FlightCapturePage />} />
          <Route path="flugbuch" element={<FlightLogPage />} />
          <Route path="telemetrie" element={<TelemetryPage />} />
          <Route path="taktik-karte" element={<TacticalMapPage />} />
          <Route path="taktik-karte-co" element={<CoPilotTacticalMapPage />} />
          {/* frühere Seite Telemetrie (Lesezeichen) – jetzt im Cockpit */}
          <Route path="telemetrie-co" element={<Navigate to="/cockpit-co" replace />} />
          <Route path="cockpit-co" element={<CoPilotCockpitPage />} />
          <Route path="missionsplaner-co" element={<CoPilotMissionsplanerPage />} />
          {/* frühere Seite Live-Video (Lesezeichen) */}
          <Route path="live-video-co" element={<Navigate to="/cockpit-co" replace />} />
          <Route path="checklisten" element={<CoPilotChecklistPage />} />
          <Route path="luftraum-beobachtung" element={<AirspaceObservationPage />} />
          <Route path="einsatz-details" element={<MissionDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
        <Route path="feedback" element={<FeedbackPage modul="pilot" />} />
        </Route>
      </Routes>
    </MissionProvider>
  )
}

function App() {
  return (
    <div className="app min-h-screen bg-dark-bg">
      <AppRoutes />
    </div>
  )
}

export default App